import { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"; // eslint-disable-line no-unused-vars
import CollapsibleCard from "../../components/CollapsibleCard";
import { Shirt } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

/**
 * Uniform options card — admins define the list of uniform types
 * (Concert Jacket, Bow Tie, Trousers...) used by the Inventory page.
 * Self-contained: fetches /band/uniform-options on mount.
 *
 * Extracted from Settings.jsx in iter-66d.
 */
const UniformOptionsCard = () => {
  const [uniformOptions, setUniformOptions] = useState([]);
  const [newUniformOption, setNewUniformOption] = useState("");
  const [savingUniformOptions, setSavingUniformOptions] = useState(false);

  const fetchUniformOptions = async () => {
    try {
      const res = await apiClient.get("/band/uniform-options");
      setUniformOptions(res.data?.options || []);
    } catch (e) {
      // Non-critical: band may not have uniforms configured yet.
      console.error("fetchUniformOptions failed:", e);
    }
  };

  useEffect(() => {
    fetchUniformOptions();
  }, []);

  const addUniformOption = async () => {
    const v = (newUniformOption || "").trim();
    if (!v) return;
    if (uniformOptions.some((o) => o.toLowerCase() === v.toLowerCase())) {
      toast.error("That option already exists");
      return;
    }
    const next = [...uniformOptions, v];
    setSavingUniformOptions(true);
    try {
      const res = await apiClient.put("/band/uniform-options", { options: next });
      setUniformOptions(res.data?.options || next);
      setNewUniformOption("");
      toast.success(`Added "${v}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSavingUniformOptions(false);
    }
  };

  const removeUniformOption = async (opt) => {
    if (!window.confirm(`Remove "${opt}" from your uniform options? Existing inventory items keep their label.`)) return;
    const next = uniformOptions.filter((o) => o !== opt);
    setSavingUniformOptions(true);
    try {
      const res = await apiClient.put("/band/uniform-options", { options: next });
      setUniformOptions(res.data?.options || next);
      toast.success(`Removed "${opt}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSavingUniformOptions(false);
    }
  };

  return (
    <CollapsibleCard icon={Shirt} title="Uniform options" testId="uniform-options-card">
      <p className="text-sm text-muted-foreground mb-4">
        Define the uniform types your band uses (e.g. Concert Jacket, Bow Tie, Trousers, Shoes).
        These appear in the Inventory page when adding a uniform item.
      </p>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 min-h-[2.5rem]" data-testid="uniform-options-chips">
          {uniformOptions.length === 0 && (
            <p className="text-sm text-muted-foreground italic">No options yet — add your first one below.</p>
          )}
          {uniformOptions.map((opt) => (
            <span
              key={opt}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-secondary/60 px-3 py-1.5 text-sm"
              data-testid={`uniform-option-${opt}`}
            >
              {opt}
              <button
                type="button"
                onClick={() => removeUniformOption(opt)}
                disabled={savingUniformOptions}
                className="text-muted-foreground hover:text-red-400 transition"
                aria-label={`Remove ${opt}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>

        <div className="flex gap-2 max-w-md">
          <Input
            value={newUniformOption}
            onChange={(e) => setNewUniformOption(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); addUniformOption(); }
            }}
            placeholder="e.g. Concert Jacket"
            maxLength={60}
            disabled={savingUniformOptions}
            data-testid="uniform-option-input"
          />
          <Button
            onClick={addUniformOption}
            disabled={savingUniformOptions || !newUniformOption.trim()}
            data-testid="uniform-option-add-btn"
          >
            Add
          </Button>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default UniformOptionsCard;
